import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useWorkflowStore } from '@/stores/workflow-store'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'

import { ConditionBuilder } from './condition-builder'

import type { FlowNode, Condition } from '@/schemas/workflow-node'

/**
 * Sheet-based side panel for configuring a selected workflow edge.
 * Opens when selectedEdgeId is not null in the workflow store.
 * Shows condition builder and branch label input for conditional edges.
 */
export function EdgeConfigPanel() {
  const { t } = useTranslation('workflows')
  const selectedEdgeId = useWorkflowStore((s) => s.selectedEdgeId)
  const flowEdges = useWorkflowStore((s) => s.flowEdges)
  const flowNodes = useWorkflowStore((s) => s.flowNodes)
  const updateEdgeCondition = useWorkflowStore((s) => s.updateEdgeCondition)
  const selectEdge = useWorkflowStore((s) => s.selectEdge)
  const onEdgesChange = useWorkflowStore((s) => s.onEdgesChange)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [condition, setCondition] = useState<Condition | null>(null)
  const [branchLabel, setBranchLabel] = useState<string>('')

  const selectedEdge = flowEdges.find((e) => e.id === selectedEdgeId)
  const edgeData = (selectedEdge?.data ?? {}) as {
    condition: Condition | null
    branch_label: string | null
  }

  useEffect(() => {
    if (!selectedEdge) return
    setCondition(edgeData.condition ?? null)
    setBranchLabel(edgeData.branch_label ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: re-sync only when selected edge id changes
  }, [selectedEdgeId])

  const sourceNode = selectedEdge
    ? flowNodes.find((n) => n.id === selectedEdge.source)
    : undefined
  const upstreamNodes = sourceNode
    ? [
        {
          id: sourceNode.id,
          label: (sourceNode.data as unknown as FlowNode).label,
          output_schema: (sourceNode.data as unknown as FlowNode)
            .output_schema,
        },
      ]
    : []

  const handleSave = useCallback(() => {
    updateEdgeCondition(selectedEdgeId!, condition, branchLabel || null)
    toast.success(t('flow.saveSuccess'))
  }, [selectedEdgeId, condition, branchLabel, updateEdgeCondition, t])

  const handleDeleteConfirm = useCallback(() => {
    onEdgesChange([{ type: 'remove', id: selectedEdgeId! }])
    selectEdge(null)
    setDeleteDialogOpen(false)
    toast.success(t('flow.deleteEdgeConfirm'))
  }, [selectedEdgeId, onEdgesChange, selectEdge, t])

  if (!selectedEdge) return null

  return (
    <>
      <Sheet
        open={!!selectedEdgeId}
        onOpenChange={(open) => {
          if (!open) selectEdge(null)
        }}
      >
        <SheetContent
          side="right"
          className="w-[480px] sm:w-[480px] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>{t('flow.sectionBranchCondition')}</SheetTitle>
          </SheetHeader>

          <div className="space-y-6 py-6 px-6">
            {/* Branch label */}
            <div>
              <Label>
                {t('flow.branchLabel', { defaultValue: 'Branch label' })}
              </Label>
              <Input
                value={branchLabel}
                onChange={(e) => setBranchLabel(e.target.value)}
                placeholder={t('flow.branchLabelPlaceholder', {
                  defaultValue: 'e.g., true, false, default',
                })}
              />
            </div>

            {/* Condition builder */}
            <div>
              <Label className="mb-2 block">
                {t('flow.conditionLabel', { defaultValue: 'Condition' })}
              </Label>
              <ConditionBuilder
                value={condition}
                onChange={setCondition}
                upstreamNodes={upstreamNodes}
              />
            </div>

            {/* Edge info */}
            <div className="text-sm text-muted-foreground">
              <p>
                {t('flow.edgeFrom', { defaultValue: 'From' })}:{' '}
                {sourceNode
                  ? (sourceNode.data as unknown as FlowNode).label
                  : selectedEdge.source}
              </p>
              <p>
                {t('flow.edgeTo', { defaultValue: 'To' })}:{' '}
                {(() => {
                  const targetNode = flowNodes.find(
                    (n) => n.id === selectedEdge.target
                  )
                  return targetNode
                    ? (targetNode.data as unknown as FlowNode).label
                    : selectedEdge.target
                })()}
              </p>
            </div>
          </div>

          <SheetFooter className="px-6 py-4 border-t border-border">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
            >
              {t('flow.deleteEdgeConfirm')}
            </Button>
            <Button variant="default" onClick={handleSave}>
              {t('flow.saveNode')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete edge confirmation dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('flow.deleteEdgeTitle')}
        description={t('flow.deleteNodeBodyNoDeps')}
        confirmLabel={t('flow.deleteEdgeConfirm')}
        confirmingLabel={t('flow.deleteEdgeConfirm')}
        confirmVariant="action-red"
        isProcessing={false}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}
